import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowRight, KeyRound, ShieldCheck } from 'lucide-react';
import { Alert, Button, Card, Field, Input } from '@/components/ui';
import { PasswordInput } from '@/components/PasswordInput';
import { ResendVerification } from '@/components/ResendVerification';
import { tokens } from '@/lib/api';
import { AuthShell } from '@/layouts/AuthShell';
import { ApiError, useAuth } from '@/lib/auth';
import { homePathFor } from '@/lib/home-path';

export default function Login() {
  const { signIn, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [needsCode, setNeedsCode] = useState(false);
  const [remember, setRemember] = useState(() => tokens.remembered());
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setLoading(true);

    try {
      localStorage.setItem('unifemga-remember', String(remember));
      const result = await signIn(email, password, totpCode || undefined);

      // An administrator who has not enrolled in 2FA is signed in with a token
      // that can reach nothing but enrolment. Send them straight there.
      if (result.requiresTwoFactorEnrolment) {
        toast.info('Set up two-factor authentication to continue.');
        navigate('/two-factor-setup', { replace: true });
        return;
      }

      const destination =
        (location.state as { from?: { pathname: string } } | null)?.from?.pathname ??
        (result.user.isAdmin ? '/admin' : '/portal');

      toast.success('Signed in.');
      navigate(destination, { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError && caught.error === 'TwoFactorRequired') {
        setNeedsCode(true);
        setError(null);
        toast.info('Enter the code from your authenticator app.');
      } else {
        setError(caught instanceof Error ? caught.message : 'Something went wrong.');
      }
    } finally {
      setLoading(false);
    }
  }

  // Already signed in. Showing the form would invite someone to type
  // credentials they do not need, and signing in again would rotate their
  // tokens for nothing.
  if (!authLoading && user) return <Navigate to={homePathFor(user)} replace />;

  return (
    <AuthShell
      title={needsCode ? 'One more step' : 'Welcome back'}
      description={
        needsCode
          ? 'Enter the six-digit code from your authenticator app.'
          : 'Sign in to manage your AAF-UNIFEMGA loans.'
      }
      footer={
        <p className="text-center text-sm text-foreground-secondary">
          Not registered yet?{' '}
          <Link to="/register" className="font-bold text-primary hover:underline">
            Create account
          </Link>
        </p>
      }
    >
      <Card className="p-6 sm:p-8">
        <form onSubmit={handleSubmit} className="space-y-5" noValidate>
          {error && (
            <Alert tone="danger">
              {error}
              {/*
                One block, not two. The button carries its own solid background
                so it still reads as something to press rather than as more of
                the error text — which is what a ghost button inside a tinted
                alert looks like.
              */}
              {/verify your email/i.test(error) && (
                <div className="mt-3">
                  <ResendVerification email={email} />
                </div>
              )}
            </Alert>
          )}

          <Field label="Email address" htmlFor="email" required>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              disabled={needsCode}
            />
          </Field>

          <Field label="Password" htmlFor="password" required>
            <PasswordInput
              id="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={needsCode}
            />
          </Field>

          <label className="flex cursor-pointer items-center gap-2.5 text-sm text-foreground">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="h-4 w-4 accent-[hsl(var(--primary))]"
            />
            Keep me signed in on this device
          </label>

          {needsCode && (
            <Field
              label="Authentication code"
              htmlFor="totp"
              hint="The 6-digit code from your authenticator app."
              required
            >
              <Input
                id="totp"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                autoFocus
                className="numeric text-center text-lg tracking-[0.5em]"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
              />
            </Field>
          )}

          <Button type="submit" full size="lg" loading={loading}>
            {needsCode ? (
              <>
                <ShieldCheck size={16} /> Verify and sign in
              </>
            ) : (
              <>
                Sign in <ArrowRight size={16} />
              </>
            )}
          </Button>

          <Link
            to="/forgot-password"
            className="inline-flex w-full items-center justify-center gap-1.5 text-sm font-semibold text-foreground-secondary transition-colors hover:text-primary"
          >
            <KeyRound size={14} /> Forgot your password?
          </Link>
        </form>
      </Card>
    </AuthShell>
  );
}
